import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import OrangeBySiteStandaloneHeader from "@/components/OrangeBySiteStandaloneHeader";
import useSEO from "@/hooks/useSEO";
import { Loader2, ShieldCheck, CheckCircle2, XCircle, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const CUR = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const fmt = (minor, cur) => `${CUR[cur] || cur + " "}${(Number(minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StateCard({ tone, icon: Icon, title, subtitle, children }) {
  const bg = { ok: "bg-white", paid: "bg-[#E9F7EC]", warn: "bg-[#FFF3E6]", err: "bg-[#FDECEC]" }[tone] || "bg-white";
  return (
    <div className={`border-2 border-black rounded-3xl p-7 md:p-9 ${bg}`} data-testid="pay-state-card">
      <div className="flex items-center gap-3">
        {Icon && <Icon className="w-6 h-6" />}
        <div className="text-[11px] font-bold tracking-[0.28em] uppercase">{title}</div>
      </div>
      {subtitle && <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-[-0.02em]" data-testid="pay-headline">{subtitle}</h1>}
      {children}
    </div>
  );
}

export default function PayPage() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  useSEO({ title: "Secure payment · OrangeBySite", description: "Secure payment page.", noIndex: true });
  const externalStatus = sp.get("status"); // success | cancelled from provider return
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/api/pay/${token}`);
      setData(r.data);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 404) setError("invalid");
      else if (st === 409) setError(e.response.data?.detail?.code || "unusable");
      else setError("failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // If provider returned success, poll for a bit to catch the webhook.
  useEffect(() => {
    if (externalStatus !== "success" || !data || data.reason === "already_paid") return;
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      await load();
      if (n > 20) clearInterval(t);
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [externalStatus, data?.reason]);

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/api/pay/${token}`, { email });
      const url = r.data?.redirect_url;
      // Phase 3I.3K — Test Mode + defensive redirect handling. When
      // Test Mode is ON the backend intentionally blocks live provider
      // calls, so `redirect_url` may be absent. Never call
      // window.location.assign with a null/empty URL — that produces
      // the "briefly redirecting then nothing" symptom.
      if (!url || typeof url !== "string") {
        setStarting(false);
        if (r.data?.test_mode) {
          setError("test_mode_active");
        } else {
          setError(r.data?.code || "no_redirect_url");
        }
        return;
      }
      window.location.assign(url);
    } catch (e) {
      setStarting(false);
      const d = e?.response?.data?.detail;
      // Detail may be an object ({code, message}) — coerce to string.
      const asStr = typeof d === "string" ? d : (d?.code || d?.message || null);
      setError(asStr || "failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF6] text-black">
      <OrangeBySiteStandaloneHeader subtitle="Secure payment" />
      <div className="max-w-2xl mx-auto px-5 md:px-6 py-10 md:py-14">
        {loading && (
          <StateCard tone="ok" icon={Loader2} title="Loading" subtitle="One moment…">
            <p className="mt-3 text-black/60 text-sm">Fetching your payment request.</p>
          </StateCard>
        )}
        {!loading && error === "invalid" && (
          <StateCard tone="err" icon={XCircle} title="Invalid link" subtitle="This payment link isn't valid.">
            <p className="mt-3 text-black/70">If you were expecting a payment request, please contact us at <a className="underline" href="mailto:hello@orangebysite.com">hello@orangebysite.com</a>.</p>
          </StateCard>
        )}
        {!loading && ["cancelled","expired"].includes(error) && (
          <StateCard tone="warn" icon={Clock} title={error === "expired" ? "Link expired" : "Link cancelled"} subtitle="This payment link is no longer active.">
            <p className="mt-3 text-black/70">Reply to your OrangeBySite email and we'll send a fresh link.</p>
          </StateCard>
        )}
        {!loading && data && data.reason === "already_paid" && (
          <StateCard tone="paid" icon={CheckCircle2} title="Payment already received" subtitle="Thanks — you're all set.">
            <div className="mt-4 grid grid-cols-2 gap-3 text-[13px]">
              <div><div className="text-[10px] tracking-[0.2em] uppercase font-bold text-black/50">Invoice</div><div className="mt-1 font-black">{data.invoice_number}</div></div>
              <div><div className="text-[10px] tracking-[0.2em] uppercase font-bold text-black/50">Payment stage</div><div className="mt-1 font-black">{data.milestone_label}</div></div>
              <div><div className="text-[10px] tracking-[0.2em] uppercase font-bold text-black/50">Amount</div><div className="mt-1 font-black">{fmt(data.amount_minor, data.currency)}</div></div>
              <div><div className="text-[10px] tracking-[0.2em] uppercase font-bold text-black/50">Balance</div><div className="mt-1 font-black">{Number(data.amount_outstanding_minor) === 0 ? "Paid in full" : fmt(data.amount_outstanding_minor, data.currency)}</div></div>
            </div>
          </StateCard>
        )}
        {!loading && data && data.usable && (
          <StateCard tone="ok" title="Payment request" subtitle={data.milestone_label}>
            {error === "test_mode_active" && (
              <div className="mt-4 p-3 rounded-2xl bg-[#FFF3E6] border border-[#FCE1C7] text-[13px] text-[#8A4A00]" data-testid="pay-test-mode-notice">
                TEST MODE is active on OrangeBySite. Live payment provider calls are blocked — no real payment will be taken. Ask the owner to disable Test Mode to complete a live payment.
              </div>
            )}
            {error && !["invalid","cancelled","expired","unusable","test_mode_active"].includes(error) && (
              <div className="mt-4 p-3 rounded-2xl bg-[#FDECEC] border border-[#F3B9A2] text-[13px] text-[#8B2A0F]" data-testid="pay-error-notice">
                We couldn't start the payment ({String(error)}). Please try again or reply to your OrangeBySite email.
              </div>
            )}
            {data.milestone_description && <p className="mt-2 text-black/70 text-[14px]">{data.milestone_description}</p>}
            <div className="mt-6 flex items-baseline gap-3">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-black/50">Amount due</div>
              <div className="font-black text-4xl md:text-5xl tracking-[-0.03em]" data-testid="pay-amount">{fmt(data.amount_minor, data.currency)}</div>
            </div>
            <div className="mt-1 text-[11px] tracking-[0.2em] uppercase text-black/50">Invoice {data.invoice_number}</div>
            <hr className="my-6 border-[#EAEAEA]" />
            <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-[13px]">
              <div className="text-black/60">Project / invoice total</div><div className="text-right font-bold">{fmt(data.invoice_total_minor, data.currency)}</div>
              <div className="text-black/60">Already paid</div><div className="text-right font-bold">{fmt(data.amount_paid_minor, data.currency)}</div>
              <div className="text-black/60">This payment</div><div className="text-right font-bold">{fmt(data.amount_minor, data.currency)}</div>
              <div className="text-black/60">Remaining after payment</div><div className="text-right font-bold">{fmt(Math.max(Number(data.invoice_total_minor) - Number(data.amount_paid_minor) - Number(data.amount_minor), 0), data.currency)}</div>
              {data.due_date && (<><div className="text-black/60">Due</div><div className="text-right font-bold">{data.due_date}</div></>)}
            </div>
            {externalStatus === "cancelled" && (
              <div className="mt-6 p-3 rounded-2xl bg-[#FFF3E6] border border-[#FCE1C7] text-[13px] text-[#8A4A00]">Payment cancelled. You can try again below.</div>
            )}
            {externalStatus === "success" && (
              <div className="mt-6 p-3 rounded-2xl bg-[#EAF7ED] border border-[#B7E4C0] text-[13px] text-[#0E5A17]" data-testid="pay-confirming-banner">Confirming payment with the provider — this refreshes automatically.</div>
            )}
            <label className="mt-6 block">
              <span className="block text-[10px] tracking-[0.24em] uppercase font-bold text-black/70 mb-1">Email for receipt (optional)</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="pay-email-input"
                placeholder="you@yourbusiness.com"
                autoComplete="email"
                className="w-full px-4 py-3 border-2 border-[#EAEAEA] rounded-2xl bg-white text-[#0A0A0A] placeholder:text-[#8A8A8A] text-[14px] focus:outline-none focus:border-[#FF6A00] focus:ring-4 focus:ring-[#FF6A00]/20 transition-colors [&:-webkit-autofill]:[-webkit-text-fill-color:#0A0A0A] [&:-webkit-autofill]:[transition:background-color_5000s_ease-in-out_0s] [&:-webkit-autofill]:[box-shadow:0_0_0_1000px_white_inset]" />
            </label>
            <button onClick={start} disabled={starting} data-testid="pay-complete-cta"
              className="mt-4 w-full px-6 py-4 rounded-full bg-black text-white font-black text-[13px] tracking-[0.2em] uppercase hover:bg-[#1A1A1A] disabled:opacity-60">
              {starting ? "Redirecting…" : "Complete payment"}
            </button>
            <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-black/50">
              <ShieldCheck className="w-3.5 h-3.5" /> Secure payment processing.
            </div>
          </StateCard>
        )}
      </div>
    </div>
  );
}
